/**
 * backgroundCatalog — Catálogo de fondos virtuales para teleconsulta.
 *
 * Los fondos se generan en Canvas a 1280×720 (16:9 HD) y se cachean como
 * Blob URLs. Esto evita el costo de descarga remota (CDN) y mantiene los
 * fondos consistentes y de aspecto profesional sin depender de assets
 * externos.
 *
 * Los previews son CSS gradients (livianos) para la grilla del picker.
 *
 * Para usar con @livekit/track-processors:
 *   const url = await getBackgroundImageUrl('biblioteca');
 *   videoTrack.setProcessor(VirtualBackground(url));
 */

export type BackgroundKind = 'none' | 'blur' | 'image';

export interface BackgroundOption {
  id: string;                 // unique identifier
  label: string;              // user-facing
  kind: BackgroundKind;
  /** Blur radius en px (sólo cuando kind === 'blur') */
  blurRadius?: number;
  /** Generador de imagen (sólo cuando kind === 'image') */
  generator?: (canvas: HTMLCanvasElement) => void;
  /** Preview CSS para el botón del picker */
  preview: string;
  /** URL remota (fondos subidos por admin, sin generator) */
  remoteUrl?: string;
}

/* ── Generadores de fondos ─────────────────────────────────────────────── */

function fillBg(ctx: CanvasRenderingContext2D, stops: [number, string][], w: number, h: number, vertical = false) {
  const grad = vertical
    ? ctx.createLinearGradient(0, 0, 0, h)
    : ctx.createLinearGradient(0, 0, w, h);
  for (const [pos, color] of stops) grad.addColorStop(pos, color);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);
}

function noise(ctx: CanvasRenderingContext2D, w: number, h: number, opacity: number) {
  const img = ctx.getImageData(0, 0, w, h);
  const data = img.data;
  const step = 8;
  for (let i = 0; i < data.length; i += step * 4) {
    const n = (Math.random() - 0.5) * 255 * opacity;
    data[i]     = Math.max(0, Math.min(255, data[i]     + n));
    data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + n));
    data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + n));
  }
  ctx.putImageData(img, 0, 0);
}

function drawConsultorio(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;

  // Pared cálida con sutil viñeta
  fillBg(ctx, [
    [0,    '#F5F0E8'],
    [0.55, '#EDE5D3'],
    [1,    '#DDD2BC'],
  ], w, h, true);

  // Piso (madera tenue)
  const floorGrad = ctx.createLinearGradient(0, h * 0.62, 0, h);
  floorGrad.addColorStop(0, '#BFA98A');
  floorGrad.addColorStop(1, '#967B59');
  ctx.fillStyle = floorGrad;
  ctx.fillRect(0, h * 0.62, w, h * 0.38);

  // Línea de zócalo
  ctx.strokeStyle = 'rgba(80, 60, 40, 0.18)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, h * 0.62);
  ctx.lineTo(w, h * 0.62);
  ctx.stroke();

  // Ventana con luz suave (lado derecho)
  ctx.fillStyle = 'rgba(255, 250, 230, 0.55)';
  ctx.fillRect(w * 0.62, h * 0.08, w * 0.27, h * 0.42);
  ctx.strokeStyle = 'rgba(160, 140, 110, 0.7)';
  ctx.lineWidth = 3;
  ctx.strokeRect(w * 0.62, h * 0.08, w * 0.27, h * 0.42);
  // Cruz de ventana
  ctx.beginPath();
  ctx.moveTo(w * 0.755, h * 0.08);
  ctx.lineTo(w * 0.755, h * 0.5);
  ctx.moveTo(w * 0.62, h * 0.29);
  ctx.lineTo(w * 0.89, h * 0.29);
  ctx.stroke();

  // Estantería (lado izquierdo)
  ctx.fillStyle = '#7E6549';
  ctx.fillRect(w * 0.05, h * 0.18, w * 0.18, h * 0.42);
  ctx.fillStyle = '#8E7456';
  for (let row = 0; row < 4; row++) {
    ctx.fillRect(w * 0.055, h * (0.20 + row * 0.105), w * 0.17, h * 0.022);
  }
  // Libros como bloques de color
  const bookColors = ['#7D2E2E', '#2E5C7D', '#3D6B3D', '#7D582E', '#5A2E5C'];
  for (let r = 0; r < 4; r++) {
    let x = w * 0.06;
    for (let b = 0; b < 6; b++) {
      ctx.fillStyle = bookColors[(r + b) % bookColors.length];
      ctx.fillRect(x, h * (0.22 + r * 0.105), w * 0.025, h * 0.08);
      x += w * 0.028;
    }
  }

  // Tinte general cálido
  ctx.fillStyle = 'rgba(245, 220, 180, 0.05)';
  ctx.fillRect(0, 0, w, h);
}

function drawOficinaModerna(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;

  // Pared (gris cálido moderno)
  fillBg(ctx, [
    [0,   '#EDE9E4'],
    [0.7, '#DAD3CA'],
    [1,   '#B8B0A4'],
  ], w, h, true);

  // Piso
  ctx.fillStyle = '#9A9180';
  ctx.fillRect(0, h * 0.68, w, h * 0.32);

  // Mueble bajo con plantas
  ctx.fillStyle = '#3F352B';
  ctx.fillRect(w * 0.08, h * 0.5, w * 0.84, h * 0.06);

  // Plantas tropicales
  ctx.fillStyle = '#2E7D32';
  ctx.beginPath();
  ctx.ellipse(w * 0.15, h * 0.46, w * 0.04, h * 0.10, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#388E3C';
  ctx.beginPath();
  ctx.ellipse(w * 0.18, h * 0.43, w * 0.035, h * 0.09, -0.2, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = '#1B5E20';
  ctx.beginPath();
  ctx.ellipse(w * 0.85, h * 0.46, w * 0.045, h * 0.11, 0, 0, Math.PI * 2);
  ctx.fill();

  // Cuadro decorativo
  ctx.fillStyle = '#0E3A6B';
  ctx.fillRect(w * 0.42, h * 0.18, w * 0.16, h * 0.22);
  ctx.strokeStyle = '#FFF';
  ctx.lineWidth = 3;
  ctx.strokeRect(w * 0.42, h * 0.18, w * 0.16, h * 0.22);
  // Detalle abstracto en el cuadro
  ctx.fillStyle = '#F5C400';
  ctx.beginPath();
  ctx.arc(w * 0.5, h * 0.29, h * 0.04, 0, Math.PI * 2);
  ctx.fill();

  // Lámpara colgante
  ctx.strokeStyle = '#3F352B';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(w * 0.78, 0);
  ctx.lineTo(w * 0.78, h * 0.15);
  ctx.stroke();
  ctx.fillStyle = '#F5C400';
  ctx.beginPath();
  ctx.ellipse(w * 0.78, h * 0.16, w * 0.04, h * 0.04, 0, 0, Math.PI * 2);
  ctx.fill();
}

function drawBiblioteca(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;

  // Fondo cálido
  fillBg(ctx, [
    [0,   '#3D2A1E'],
    [0.6, '#5A3B26'],
    [1,   '#7A5236'],
  ], w, h, true);

  // Pared de estanterías (full width, varias filas)
  const shelfRows = 6;
  for (let row = 0; row < shelfRows; row++) {
    const yTop = h * (0.05 + row * 0.14);
    const shelfHeight = h * 0.12;
    // Madera del estante
    ctx.fillStyle = '#3D2A1E';
    ctx.fillRect(0, yTop + shelfHeight, w, h * 0.02);
    // Libros en este nivel
    let x = w * 0.02;
    const bookPalette = ['#5C2E2E', '#2E4E5C', '#3D5C2E', '#5C462E', '#4A2E5C', '#6E4A2E', '#2E5C4A'];
    while (x < w * 0.98) {
      const bookW = w * (0.018 + Math.random() * 0.022);
      const bookH = shelfHeight * (0.85 + Math.random() * 0.15);
      ctx.fillStyle = bookPalette[Math.floor(Math.random() * bookPalette.length)];
      ctx.fillRect(x, yTop + (shelfHeight - bookH), bookW, bookH);
      // Tira dorada en algunos
      if (Math.random() > 0.6) {
        ctx.fillStyle = 'rgba(245, 196, 0, 0.4)';
        ctx.fillRect(x, yTop + (shelfHeight - bookH) + bookH * 0.5, bookW, 2);
      }
      x += bookW + 1;
    }
  }

  // Viñeta oscura para profundidad
  const vignette = ctx.createRadialGradient(w / 2, h / 2, w * 0.3, w / 2, h / 2, w * 0.7);
  vignette.addColorStop(0, 'rgba(0, 0, 0, 0)');
  vignette.addColorStop(1, 'rgba(0, 0, 0, 0.35)');
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, w, h);
}

function drawPaisaje(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;

  // Cielo amanecer suave
  fillBg(ctx, [
    [0,    '#1A2B47'],
    [0.35, '#5B6E8E'],
    [0.6,  '#D4A78F'],
    [1,    '#F5D5A6'],
  ], w, h, true);

  // Sol difuso
  const sunGrad = ctx.createRadialGradient(w * 0.72, h * 0.45, 0, w * 0.72, h * 0.45, w * 0.18);
  sunGrad.addColorStop(0, 'rgba(255, 240, 200, 0.85)');
  sunGrad.addColorStop(0.6, 'rgba(255, 200, 130, 0.35)');
  sunGrad.addColorStop(1, 'rgba(255, 200, 130, 0)');
  ctx.fillStyle = sunGrad;
  ctx.fillRect(0, 0, w, h);

  // Montañas lejanas (azul niebla)
  ctx.fillStyle = '#46587A';
  ctx.beginPath();
  ctx.moveTo(0, h * 0.62);
  ctx.lineTo(w * 0.15, h * 0.45);
  ctx.lineTo(w * 0.32, h * 0.55);
  ctx.lineTo(w * 0.50, h * 0.40);
  ctx.lineTo(w * 0.68, h * 0.50);
  ctx.lineTo(w * 0.85, h * 0.42);
  ctx.lineTo(w, h * 0.50);
  ctx.lineTo(w, h * 0.62);
  ctx.closePath();
  ctx.fill();

  // Montañas medias
  ctx.fillStyle = '#2F4060';
  ctx.beginPath();
  ctx.moveTo(0, h * 0.68);
  ctx.lineTo(w * 0.20, h * 0.55);
  ctx.lineTo(w * 0.40, h * 0.62);
  ctx.lineTo(w * 0.60, h * 0.50);
  ctx.lineTo(w * 0.80, h * 0.58);
  ctx.lineTo(w, h * 0.55);
  ctx.lineTo(w, h * 0.68);
  ctx.closePath();
  ctx.fill();

  // Lago / piso
  fillBg(ctx, [
    [0, '#1F2F47'],
    [1, '#0D1A2D'],
  ], w, h, false);
  // Solo abajo
  ctx.fillStyle = '#1F2F47';
  ctx.fillRect(0, h * 0.68, w, h * 0.32);
  // Reflejos
  ctx.fillStyle = 'rgba(255, 230, 180, 0.08)';
  ctx.fillRect(0, h * 0.68, w, 2);
  ctx.fillRect(0, h * 0.72, w * 0.4, 1);

  noise(ctx, w, h, 0.04);
}

function drawCielo(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;

  // Cielo despejado
  fillBg(ctx, [
    [0,    '#7FC1E8'],
    [0.6,  '#B9DDF0'],
    [1,    '#F0F8FC'],
  ], w, h, true);

  // Nubes
  const drawCloud = (cx: number, cy: number, scale: number, alpha: number) => {
    ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
    ctx.beginPath();
    ctx.ellipse(cx,            cy,            scale * 60, scale * 24, 0, 0, Math.PI * 2);
    ctx.ellipse(cx + scale*40, cy - scale*10, scale * 50, scale * 22, 0, 0, Math.PI * 2);
    ctx.ellipse(cx - scale*40, cy - scale*5,  scale * 45, scale * 20, 0, 0, Math.PI * 2);
    ctx.ellipse(cx + scale*15, cy + scale*8,  scale * 55, scale * 22, 0, 0, Math.PI * 2);
    ctx.fill();
  };

  drawCloud(w * 0.18, h * 0.25, w / 700, 0.85);
  drawCloud(w * 0.55, h * 0.18, w / 600, 0.75);
  drawCloud(w * 0.82, h * 0.32, w / 800, 0.65);
  drawCloud(w * 0.30, h * 0.55, w / 750, 0.55);
  drawCloud(w * 0.70, h * 0.65, w / 700, 0.50);
}

function drawAbstracto(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;

  // Verde médico institucional (Sage Stone palette)
  fillBg(ctx, [
    [0,   '#083D20'],
    [0.55, '#0B5429'],
    [1,    '#1A8048'],
  ], w, h, false);

  // Manchas radiales doradas (corporativo)
  const goldGrad1 = ctx.createRadialGradient(w * 0.15, h * 0.25, 0, w * 0.15, h * 0.25, w * 0.5);
  goldGrad1.addColorStop(0, 'rgba(245, 196, 0, 0.22)');
  goldGrad1.addColorStop(1, 'rgba(245, 196, 0, 0)');
  ctx.fillStyle = goldGrad1;
  ctx.fillRect(0, 0, w, h);

  const goldGrad2 = ctx.createRadialGradient(w * 0.85, h * 0.75, 0, w * 0.85, h * 0.75, w * 0.5);
  goldGrad2.addColorStop(0, 'rgba(125, 212, 160, 0.20)');
  goldGrad2.addColorStop(1, 'rgba(125, 212, 160, 0)');
  ctx.fillStyle = goldGrad2;
  ctx.fillRect(0, 0, w, h);

  // Línea decorativa diagonal sutil
  ctx.strokeStyle = 'rgba(245, 196, 0, 0.12)';
  ctx.lineWidth = 1.5;
  for (let i = 0; i < 20; i++) {
    ctx.beginPath();
    ctx.moveTo(0, (h / 20) * i + h * 0.1);
    ctx.lineTo(w, (h / 20) * i - h * 0.1);
    ctx.stroke();
  }

  noise(ctx, w, h, 0.03);
}

function drawNeutro(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;

  // Gradient gris cálido suave
  fillBg(ctx, [
    [0,   '#E8E4DE'],
    [0.5, '#D3CCC0'],
    [1,   '#B5ACA0'],
  ], w, h, true);

  // Sombra sutil de pared
  ctx.fillStyle = 'rgba(120, 100, 80, 0.06)';
  ctx.fillRect(0, h * 0.5, w, h * 0.5);

  noise(ctx, w, h, 0.03);
}

/* ── Catálogo de opciones ──────────────────────────────────────────────── */

export const BACKGROUND_CATALOG: BackgroundOption[] = [
  {
    id: 'none',
    label: 'Sin fondo',
    kind: 'none',
    preview: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
  },
  {
    id: 'blur-light',
    label: 'Desenfoque',
    kind: 'blur',
    blurRadius: 10,
    preview: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.2), rgba(255,255,255,0.05))',
  },
  {
    id: 'blur-strong',
    label: 'Desenfoque +',
    kind: 'blur',
    blurRadius: 20,
    preview: 'radial-gradient(circle at 50% 50%, rgba(180,180,200,0.4), rgba(80,80,100,0.2))',
  },
  {
    id: 'neutro',
    label: 'Neutro',
    kind: 'image',
    generator: drawNeutro,
    preview: 'linear-gradient(180deg, #E8E4DE 0%, #B5ACA0 100%)',
  },
  {
    id: 'consultorio',
    label: 'Consultorio',
    kind: 'image',
    generator: drawConsultorio,
    preview: 'linear-gradient(135deg, #F5F0E8 0%, #BFA98A 100%)',
  },
  {
    id: 'oficina',
    label: 'Oficina',
    kind: 'image',
    generator: drawOficinaModerna,
    preview: 'linear-gradient(180deg, #EDE9E4 0%, #9A9180 100%)',
  },
  {
    id: 'biblioteca',
    label: 'Biblioteca',
    kind: 'image',
    generator: drawBiblioteca,
    preview: 'linear-gradient(180deg, #3D2A1E 0%, #7A5236 100%)',
  },
  {
    id: 'paisaje',
    label: 'Paisaje',
    kind: 'image',
    generator: drawPaisaje,
    preview: 'linear-gradient(180deg, #1A2B47 0%, #F5D5A6 100%)',
  },
  {
    id: 'cielo',
    label: 'Cielo',
    kind: 'image',
    generator: drawCielo,
    preview: 'linear-gradient(180deg, #7FC1E8 0%, #F0F8FC 100%)',
  },
  {
    id: 'minsalud',
    label: 'MINSA',
    kind: 'image',
    generator: drawAbstracto,
    preview: 'linear-gradient(135deg, #083D20 0%, #1A8048 100%)',
  },
];

/* ── Cache de Blob URLs por opción ─────────────────────────────────────── */

const urlCache = new Map<string, string>();

/** Exporta un canvas como Blob URL JPEG (opaco) y lo cachea por id. */
function canvasToBlobUrl(id: string, canvas: HTMLCanvasElement): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        const url = URL.createObjectURL(blob);
        urlCache.set(id, url);
        resolve(url);
      },
      'image/jpeg',
      0.85,
    );
  });
}

/**
 * Normaliza una imagen remota (fondo subido por el admin) al MISMO formato
 * que los fondos del catálogo: la dibuja en modo "cover" sobre un canvas
 * 1280×720 y la exporta como Blob JPEG opaco de mismo origen.
 *
 * Motivo: pasar la URL remota cruda al processor de @livekit/track-processors
 * provocaba un fondo NEGRO (el médico desaparecía). Las imágenes subidas por
 * el admin suelen ser PNG con canal alfa y de aspecto cuadrado (p. ej.
 * 1024×1024); el pipeline WebGL del processor (resizeImageToCover →
 * createImageBitmap) fallaba y caía a su "black background". Renormalizarlas a
 * un JPEG 16:9 opaco y local elimina el alfa, el aspecto cuadrado y cualquier
 * particularidad de la imagen remota, replicando la ruta probada del catálogo.
 */
async function remoteImageToBlobUrl(
  id: string,
  remoteUrl: string,
  width: number,
  height: number,
): Promise<string | null> {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('No se pudo cargar la imagen del fondo'));
    img.src = remoteUrl;
  });

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // Fondo negro base por si la fuente tuviera alfa (evita transparencias).
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, width, height);

  // Dibujo "cover": escala para llenar el canvas manteniendo aspecto y
  // recortando el excedente (centrado).
  const scale = Math.max(width / img.width, height / img.height);
  const dw = img.width * scale;
  const dh = img.height * scale;
  const dx = (width - dw) / 2;
  const dy = (height - dh) / 2;
  ctx.drawImage(img, dx, dy, dw, dh);

  return await canvasToBlobUrl(id, canvas);
}

/**
 * Genera (o recupera del cache) un Blob URL para la imagen del fondo.
 * Resoluciones por defecto: 1280×720 (HD 16:9) — suficiente para el
 * compositing del processor sin sobrecargar memoria.
 */
export async function getBackgroundImageUrl(
  option: BackgroundOption,
  width = 1280,
  height = 720,
): Promise<string | null> {
  if (option.kind !== 'image') return null;

  if (urlCache.has(option.id)) return urlCache.get(option.id)!;

  // Fondos subidos por el admin (URL remota): normalizar a Blob local 16:9
  // opaco (misma ruta que el catálogo). Si falla la normalización, se cae a la
  // URL remota cruda como último recurso.
  if (option.remoteUrl) {
    try {
      const normalized = await remoteImageToBlobUrl(option.id, option.remoteUrl, width, height);
      if (normalized) return normalized;
    } catch (e) {
      console.error('[backgroundCatalog] No se pudo normalizar el fondo remoto:', e);
    }
    return option.remoteUrl;
  }

  if (!option.generator) return null;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  option.generator(canvas);

  return await canvasToBlobUrl(option.id, canvas);
}

/**
 * Libera todos los Blob URLs creados.
 * Llamar al desmontar para evitar memory leaks.
 */
export function clearBackgroundCache() {
  for (const url of urlCache.values()) URL.revokeObjectURL(url);
  urlCache.clear();
}
